/**
 * failureTone
 *
 * The colour of the failed-request badge: a ramp from the panel's neutral grey at
 * 0% to a burgundy at 10%, saturating there.
 *
 * **Why a function and not three CSS classes.** The point of the colour is that it
 * is read without arithmetic — a number on its own is a number, and "42 failed"
 * says nothing until it is compared with how many requests were made. A ramp makes
 * the ratio visible at a glance, and a ramp that steps (grey / amber / red) throws
 * away the difference between 4% and 9%. So the interpolation is continuous and it
 * is a pure function, which is also what makes it testable without a browser.
 *
 * **Why it saturates at 10%.** Above 10% the question stops being "how bad is it"
 * and becomes "something is broken", and the customer needs a colour that says
 * that rather than a colour that keeps getting darker. The badge still prints the
 * true percentage — a 40% failure rate must never be displayed as 10% — only the
 * colour stops moving.
 *
 * **Why grey at zero.** An integration with no failures is unremarkable, not
 * good news; a green badge would compete with the change series, which is the one
 * thing on this card that means "something happened". Grey keeps the ramp in one
 * hue family and leaves green meaning one thing.
 *
 * The stops are literal colours rather than tokens because the ramp is computed,
 * not selected: a token can be swapped by a stylesheet, an interpolation cannot.
 */

/** FAILURE_RAMP_MAX is the share at which the colour stops changing. */
export const FAILURE_RAMP_MAX = 0.1;

/**
 * The ramp's stops, in sRGB. The middle stop is a dusty rose rather than a
 * half-way mix of grey and burgundy: a straight line between the two passes
 * through a muddy green-grey, and the ramp has to stay legible at 3–5%, which is
 * where a partly-broken integration actually sits.
 */
const RAMP = [
  { at: 0, rgb: [154, 160, 171] }, // --text-muted
  { at: 0.05, rgb: [168, 107, 122] },
  { at: FAILURE_RAMP_MAX, rgb: [123, 31, 43] }, // burgundy
];

/**
 * failureRate turns the pair of counters into a share in 0..1.
 *
 * It takes the two numbers rather than one so the 0/0 case has a defined answer
 * here. A window with no requests is not a failed window, and a NaN would reach
 * the colour ramp and produce `rgb(NaN, NaN, NaN)` — an invisible badge for the
 * one customer who has had no traffic yet.
 */
export function failureRate(failed, requests) {
  const requested = Number(requests);
  const broken = Number(failed);
  if (!Number.isFinite(requested) || requested <= 0) return 0;
  if (!Number.isFinite(broken) || broken <= 0) return 0;
  return broken / requested;
}

/** lerp mixes two colours; `t` is clamped to 0..1 by the caller. */
function lerp(from, to, t) {
  const channel = (index) => Math.round(from[index] + (to[index] - from[index]) * t);
  return [channel(0), channel(1), channel(2)];
}

/**
 * failureTone maps a share in 0..1 onto the ramp's CSS colour.
 *
 * Values below zero and above FAILURE_RAMP_MAX are clamped, and a value that is
 * not a number is treated as zero: this function's output goes straight into a
 * style attribute, so anything it cannot interpret has to become the neutral
 * colour rather than an invalid one.
 */
export function failureTone(rate) {
  const value = Number(rate);
  const share = Number.isFinite(value) ? Math.min(Math.max(value, 0), FAILURE_RAMP_MAX) : 0;

  let rgb = RAMP[0].rgb;
  for (let index = 1; index < RAMP.length; index++) {
    const upper = RAMP[index];
    const lower = RAMP[index - 1];
    if (share <= upper.at) {
      const span = upper.at - lower.at;
      rgb = lerp(lower.rgb, upper.rgb, span === 0 ? 0 : (share - lower.at) / span);
      break;
    }
    // Past the last stop the clamp above has already made this unreachable, but
    // holding the top colour is the right answer if the ramp ever loses a stop.
    rgb = upper.rgb;
  }
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

/**
 * formatFailureShare prints a share the way the badge shows it.
 *
 * One decimal below 10% — the range where a tenth of a percent is the difference
 * between "as designed" and "one feed is down" — and none above it, where the
 * extra digit is noise. A share that is not zero but rounds to zero is printed as
 * `<0.1%` rather than `0.0%`: the badge sits next to a non-zero failed count, and
 * the two must not appear to contradict each other.
 */
export function formatFailureShare(rate) {
  const value = Number(rate);
  const share = Number.isFinite(value) && value > 0 ? value : 0;
  const percent = Math.min(share, 1) * 100;
  if (percent === 0) return "0%";
  if (percent < 0.1) return "<0.1%";
  if (percent < 10) return `${percent.toFixed(1)}%`;
  return `${percent.toFixed(0)}%`;
}
